import { DOCUMENT } from '@angular/common';
import { inject, Injectable, Renderer2, RendererFactory2, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, of, shareReplay } from 'rxjs';
import { environment } from '@core/envs';

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsScriptLoaderService {
  readonly #document = inject(DOCUMENT);
  readonly #renderer: Renderer2 = inject(RendererFactory2).createRenderer(null, null);
  readonly #sanitizer = inject(DomSanitizer);
  readonly #scriptSelector = 'script[data-google-maps-script="true"]';
  readonly #scriptUrl = `${environment.getRoute('google', false, true)}/public/maps/api/js`;

  #loader$?: Observable<void>;

  load(): Observable<void> {
    if (this.#isGoogleMapsAvailable()) {
      return of(void 0);
    }

    if (!this.#loader$) {
      this.#loader$ = new Observable<void>((observer) => {
        let scriptUrl: string;

        try {
          scriptUrl = this.#getScriptUrl();
        } catch (error) {
          this.#loader$ = undefined;
          observer.error(error);
          return;
        }

        let script = this.#document.querySelector<HTMLScriptElement>(this.#scriptSelector);

        if (script && !this.#isTrustedScriptElement(script, scriptUrl)) {
          this.#removeScriptElement(script);
          script = null;
        }

        if (!script) {
          script = this.#appendTrustedScript(scriptUrl);
        }

        if (script.dataset['loaded'] === 'true' || this.#isGoogleMapsAvailable()) {
          observer.next();
          observer.complete();
          return;
        }

        const handleLoad = () => {
          script!.dataset['loaded'] = 'true';
          observer.next();
          observer.complete();
        };

        const handleError = () => {
          if (script) {
            this.#removeScriptElement(script);
          }
          this.#loader$ = undefined;
          observer.error(new Error('Google Maps script could not be loaded.'));
        };

        script.addEventListener('load', handleLoad, { once: true });
        script.addEventListener('error', handleError, { once: true });

        return () => {
          script?.removeEventListener('load', handleLoad);
          script?.removeEventListener('error', handleError);
        };
      }).pipe(
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.#loader$;
  }

  #isGoogleMapsAvailable(): boolean {
    return typeof google !== 'undefined' && !!google.maps;
  }

  #appendTrustedScript(scriptUrl: string): HTMLScriptElement {
    const script = this.#renderer.createElement('script') as HTMLScriptElement;

    this.#renderer.setAttribute(script, 'type', 'text/javascript');
    this.#renderer.setAttribute(script, 'src', scriptUrl);
    this.#renderer.setAttribute(script, 'crossorigin', 'anonymous');
    this.#renderer.setAttribute(script, 'data-google-maps-script', 'true');
    this.#renderer.setProperty(script, 'async', true);
    this.#renderer.setProperty(script, 'defer', true);
    this.#renderer.appendChild(this.#document.head, script);

    return script;
  }

  #removeScriptElement(script: HTMLScriptElement): void {
    const parentNode = script.parentNode ?? this.#document.head;

    this.#renderer.removeChild(parentNode, script);
  }

  #getScriptUrl(): string {
    const sanitizedUrl = this.#sanitizer.sanitize(SecurityContext.URL, this.#scriptUrl) ?? '';

    if (!sanitizedUrl || sanitizedUrl !== this.#scriptUrl || sanitizedUrl.startsWith('unsafe:')) {
      throw new Error('Google Maps script URL is invalid.');
    }

    return sanitizedUrl;
  }

  #isTrustedScriptElement(script: HTMLScriptElement, trustedScriptUrl: string): boolean {
    const scriptSource = script.getAttribute('src') ?? script.src;

    if (!scriptSource) {
      return false;
    }

    return scriptSource === trustedScriptUrl || script.src === trustedScriptUrl;
  }
}
